import { DISPLAY_TZ, DISPLAY_LOCALE } from "../config";

/**
 * Convert a UTC seconds timestamp into "YYYY-MM-DD"
 * using the display time zone.
 */
export function dayKey(sec: number, timeZone: string = DISPLAY_TZ): string {
  return new Date(sec * 1000).toLocaleDateString(DISPLAY_LOCALE, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/**
 * Today’s key in display TZ.
 */
export function todayKey(timeZone: string = DISPLAY_TZ): string {
  return new Date().toLocaleDateString(DISPLAY_LOCALE, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/**
 * Tomorrow’s key in display TZ.
 */
export function tomorrowKey(timeZone: string = DISPLAY_TZ): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString(DISPLAY_LOCALE, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/** True if string already has an explicit zone (Z or ±HH:MM) */
const hasExplicitZone = (s: string) => /Z$|[+\-]\d{2}:\d{2}$/.test(s);

/** Normalize "YYYY-MM-DD[ T]HH:mm[:ss]" -> "YYYY-MM-DDTHH:mm[:ss]" */
const normalizeLocalISO = (s: string) => {
  const trimmed = s.trim();
  // allow "YYYY-MM-DD" only → midnight
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return `${trimmed}T00:00:00`;
  // allow space between date/time
  return trimmed.replace(" ", "T");
};

/** Build a Date that represents the local wall time in the given IANA time zone. */
export function makeDateInTZ(localISO: string, timeZone: string = DISPLAY_TZ): Date {
  const s = normalizeLocalISO(localISO);
  // Parse into parts
  const [d, t = "00:00:00"] = s.split("T");
  const [Y, M, D] = d.split("-").map(Number);
  const [h, m, ssec = 0] = t.split(":").map(Number);

  // First guess UTC instant (assume provided wall time is UTC)
  const guessUTC = Date.UTC(Y, M - 1, D, h, m, ssec);

  // See what that UTC instant looks like in the target TZ
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(new Date(guessUTC)).map(p => [p.type, p.value])
  );
  const shownAsUTC = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second)
  );

  // Offset (ms) between wall-time-as-UTC and true UTC at that instant
  const offsetMs = shownAsUTC - guessUTC;

  // Subtract offset so the final UTC instant corresponds to desired local wall time
  return new Date(Date.UTC(Y, M - 1, D, h, m, ssec) - offsetMs);
}

/** Parse user-entered date/time.
 *  - If it already has Z/±HH:MM → use native Date.
 *  - If numeric → treat as epoch (sec or ms).
 *  - Else interpret as local wall time in `timeZone` (default DISPLAY_TZ).
 */
export function parseLocalInTZ(input: string, timeZone: string = DISPLAY_TZ): Date | undefined {
  if (!input) return undefined;
  const s = input.trim();

  // Zoned string? Let JS handle it.
  if (hasExplicitZone(s)) {
    const d = new Date(s);
    return Number.isNaN(+d) ? undefined : d;
  }

  // Numeric? epoch seconds or ms
  if (/^\d{9,13}$/.test(s)) {
    const n = Number(s);
    return new Date(n < 1e12 ? n * 1000 : n);
  }

  // Otherwise, treat as wall time in the provided tz
  const d = makeDateInTZ(s, timeZone);
  return Number.isNaN(+d) ? undefined : d;
}
